import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import './Heist.css';

// ─── Tuning constants ──────────────────────────────────────────────────────
const ARENA = 14;            // half-extent of playable floor
const PLAYER_SPEED = 5.2;
const GUARD_SPEED = 3.0;
const VIEW_RANGE = 6;
const HALF_FOV = THREE.MathUtils.degToRad(27);
const PROXIMITY_BUST = 1.7;  // walking this close to a guard = instantly seen
const ALARM_MAX = 100;
const ALARM_RISE = 42;       // per second while seen
const ALARM_DECAY = 16;      // per second while unseen (slow — tension lingers)

// Named alarm tiers for the HUD readout.
type AlarmTier = 'clear' | 'suspect' | 'alarm' | 'lockdown';
function alarmTierAt(alarm: number, policeCalled: boolean): AlarmTier {
  if (policeCalled || alarm >= ALARM_MAX) return 'lockdown';
  if (alarm >= ALARM_TRIP) return 'alarm';
  if (alarm > 8) return 'suspect';
  return 'clear';
}
const TIME_LIMIT = 90;       // seconds of real play = the bank's lunch→close→after-hours window

// ── Day clock ────────────────────────────────────────────────────────────────
// The world runs on a clock. One real second = CLOCK_RATE game-minutes, derived
// from elapsed play time so the sim stays deterministic (no separate counter to
// drift). The bank opens busy at lunch, shoos everyone out at close, then goes
// dark and the guards take over.
const CLOCK_START = 12 * 60;       // 12:00 (game-minutes since midnight)
const CLOCK_RATE = 1;              // game-minutes per real second → 12:00 → 13:30 over 90s
const CLOCK_CLOSING = 12 * 60 + 30; // 12:30 — tills close, customers + staff file out
const CLOCK_AFTERHOURS = 12 * 60 + 45; // 12:45 — doors locked, lights down, guards sweep

type DayPhase = 'open' | 'closing' | 'afterHours';
function dayPhaseAt(clock: number): DayPhase {
  if (clock < CLOCK_CLOSING) return 'open';
  if (clock < CLOCK_AFTERHOURS) return 'closing';
  return 'afterHours';
}
function formatClock(clock: number): string {
  const h = Math.floor(clock / 60) % 24;
  const m = Math.floor(clock % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}
const VAULT_POS = new THREE.Vector3(0, 0, -9);
const CRACK_RADIUS = 2.3;
const VAULT_COLLIDE = 1.7;   // thief stops at the vault face (also the crack distance)
const CRACK_TIME = 2.6;      // seconds of exposure to open the vault

// ── Objective chain: steal the key → crack the vault → grab loot → exfil ──────
// The branch manager keeps the vault key on the officer's desk out on the
// platform (staff side — so grabbing it means trespassing under their nose). The
// vault won't budge without it.
const KEY_POS = new THREE.Vector3(-4.5, 0, 3);
const KEY_GRAB_RADIUS = 2.2;
// A tripped alarm summons the police: once it crosses ALARM_TRIP the response
// clock starts, and if it runs out before you're gone, you're caught.
const ALARM_TRIP = 55;
const POLICE_TIME = 28;      // seconds from silent alarm to the response arriving
const SPAWN = new THREE.Vector3(0, 0, 9);
const EXIT_Z = 8;            // z >= this (with loot) = escaped
const PLAYER_R = 0.45;

// Third-person follow camera. The thief moves toward -Z, so "behind" is +Z; the
// camera sits above and behind and tracks the thief. Portrait phones use a
// steeper, closer rig so the floor fills the screen and the view never sees past
// the walls into the background.
const CAM_OFFSET = new THREE.Vector3(0, 11, 12);          // landscape (≈42° down)
const CAM_OFFSET_PORTRAIT = new THREE.Vector3(0, 16, 7.5); // portrait (≈65° down)
const CAM_LOOK_AHEAD = 3;    // look this far ahead of the thief (-Z) so the path is visible
const CAM_LERP = 5;          // follow stiffness (higher = snappier)

type Phase = 'ready' | 'infiltrate' | 'cracking' | 'escape' | 'won' | 'caught';

interface HeistInput {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
}

interface GuardDef {
  a: [number, number];   // waypoint A (x,z)
  b: [number, number];   // waypoint B (x,z)
  startT: number;        // 0..1 phase along A→B at game start
  dir: 1 | -1;           // initial travel direction
}

// Two guards sweeping the corridor between spawn and vault. Their lanes sit
// clear of the vault-crack zone so a still thief at the vault isn't trivially seen.
const GUARDS: GuardDef[] = [
  { a: [-8, -2.5], b: [8, -2.5], startT: 0.5, dir: 1 },
  { a: [8, -4.5], b: [-8, -4.5], startT: 0.5, dir: 1 },
];

// Grand banking-hall columns: cover + line-of-sight occluders + visual rhythm.
// Kept clear of the x≈0 center spine and the guard sweep lanes.
const PILLARS: Array<[number, number]> = [
  [-6, 3.5], [6, 3.5], [-6, -0.5], [6, -0.5],
];
const PILLAR_R = 0.55;

// ── Bank floor plan: a real branch, zoned front (public) → back (vault) ───────
// Camera looks down -Z, so +Z (near, bottom of screen) is the public entrance and
// -Z (far, top) is the secured vault. Interior walls are LOW so the angled top-down
// camera sees over them into every room (a "dollhouse" view), and a clear central
// spine (|x| < SPINE) runs entrance → teller gate → hall → vault doorway → vault so
// the floor is always navigable. Boxes flagged `occludes` break a guard's line of
// sight; every box is solid (the thief collides with it).
type BoxKind = 'wall' | 'counter' | 'desk' | 'vaultwall' | 'atm' | 'seat' | 'sdbox';
interface BoxObs { cx: number; cz: number; hx: number; hz: number; h: number; occludes: boolean; kind: BoxKind }
// Central walkway kept clear of obstacles spans |x| < 1.7 (the gap baked into the
// teller line and vault-wall coordinates below) so the bank is always navigable.
const H_WALL = 1.5;      // interior partition height (see-over-able from above)
const H_VWALL = 2.3;     // vault-room walls read as heavier/secure

const BOXES: BoxObs[] = [
  // ── Teller line (z≈6.6): long counter splitting public lobby from staff hall,
  //    with a center swing-gate gap on the spine to slip behind it ──
  { cx: -7.6, cz: 6.6, hx: 5.9, hz: 0.45, h: 1.15, occludes: true, kind: 'counter' },
  { cx: 7.6, cz: 6.6, hx: 5.9, hz: 0.45, h: 1.15, occludes: true, kind: 'counter' },

  // ── ATM vestibule / entrance (z≈12.5): kiosks flanking the doors, seats ──
  { cx: -3.5, cz: 12.5, hx: 1.0, hz: 0.5, h: 1.6, occludes: true, kind: 'atm' },
  { cx: 3.5, cz: 12.5, hx: 1.0, hz: 0.5, h: 1.6, occludes: true, kind: 'atm' },
  { cx: -8, cz: 11.5, hx: 1.4, hz: 0.45, h: 0.5, occludes: false, kind: 'seat' },
  { cx: 8, cz: 11.5, hx: 1.4, hz: 0.45, h: 0.5, occludes: false, kind: 'seat' },

  // ── Office 1 — front-left private office (room x[-14,-9], z[2,6.2]),
  //    doorway in the right wall at z[3.6,4.8] ──
  { cx: -9, cz: 2.8, hx: 0.25, hz: 0.8, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: -9, cz: 5.5, hx: 0.25, hz: 0.7, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: -11.5, cz: 2, hx: 2.5, hz: 0.25, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: -11.5, cz: 4.3, hx: 1.0, hz: 0.55, h: 0.8, occludes: true, kind: 'desk' },

  // ── Office 2 — mid-left consultation room (room x[-14,-9], z[-5.5,-0.5]),
  //    doorway in the right wall at z[-3.6,-2.4] ──
  { cx: -9, cz: -4.55, hx: 0.25, hz: 0.95, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: -9, cz: -1.45, hx: 0.25, hz: 0.95, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: -11.5, cz: -5.5, hx: 2.5, hz: 0.25, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: -11.5, cz: -0.5, hx: 2.5, hz: 0.25, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: -11.5, cz: -3, hx: 1.0, hz: 0.55, h: 0.8, occludes: true, kind: 'desk' },

  // ── Safe-deposit room — right side (room x[9,14], z[-5.5,6.2]),
  //    doorway in the left wall at z[0,1.4], rows of deposit boxes inside ──
  { cx: 9, cz: -2.75, hx: 0.25, hz: 2.75, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: 9, cz: 3.8, hx: 0.25, hz: 2.4, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: 11.5, cz: -5.5, hx: 2.5, hz: 0.25, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: 11.5, cz: 6.2, hx: 2.5, hz: 0.25, h: H_WALL, occludes: true, kind: 'wall' },
  { cx: 12.6, cz: -3, hx: 1.2, hz: 0.4, h: 1.3, occludes: true, kind: 'sdbox' },
  { cx: 12.6, cz: 0.5, hx: 1.2, hz: 0.4, h: 1.3, occludes: true, kind: 'sdbox' },
  { cx: 12.6, cz: 4, hx: 1.2, hz: 0.4, h: 1.3, occludes: true, kind: 'sdbox' },

  // ── Platform / officer desks out in the hall (cover, off the spine) ──
  { cx: -4.5, cz: 3, hx: 1.1, hz: 0.7, h: 0.8, occludes: true, kind: 'desk' },
  { cx: 4.5, cz: 3, hx: 1.1, hz: 0.7, h: 0.8, occludes: true, kind: 'desk' },

  // ── Vault room — secured back-of-house. Front wall at z≈-6.6 with a center
  //    doorway (the secured corridor); shelving cabinets inside ──
  { cx: -7.6, cz: -6.6, hx: 5.9, hz: 0.3, h: H_VWALL, occludes: true, kind: 'vaultwall' },
  { cx: 7.6, cz: -6.6, hx: 5.9, hz: 0.3, h: H_VWALL, occludes: true, kind: 'vaultwall' },
  { cx: -4.5, cz: -11.5, hx: 1.0, hz: 0.6, h: 1.3, occludes: true, kind: 'sdbox' },
  { cx: 4.5, cz: -11.5, hx: 1.0, hz: 0.6, h: 1.3, occludes: true, kind: 'sdbox' },
];

// Per-kind appearance so each fixture reads at a glance from the top-down camera.
const BOX_MAT: Record<BoxKind, { color: string; metalness: number; roughness: number }> = {
  wall: { color: '#e7e1d3', metalness: 0.04, roughness: 0.8 },   // light interior partition
  counter: { color: '#6b513a', metalness: 0.2, roughness: 0.5 }, // warm wood teller line
  desk: { color: '#5b4632', metalness: 0.2, roughness: 0.5 },    // wood desks
  vaultwall: { color: '#b9bdc4', metalness: 0.5, roughness: 0.5 },// heavier steel-grey
  atm: { color: '#3a4150', metalness: 0.4, roughness: 0.4 },     // dark kiosk
  seat: { color: '#7a4a3a', metalness: 0.1, roughness: 0.7 },    // leather bench
  sdbox: { color: '#9aa3ad', metalness: 0.7, roughness: 0.35 },  // brushed-metal box bank
};

// ── The living population ─────────────────────────────────────────────────────
// Capsule "people" who actually use the bank: tellers working the line, a manager
// pacing the platform, customers milling in the lobby. Each runs a deterministic
// routine (fixed waypoint loop) during OPEN, then files out toward the doors as
// the bank closes — so by AFTER-HOURS the floor is empty and only guards remain.
// Phase A keeps them purely atmospheric; their senses arrive in Phase B.
type NpcKind = 'teller' | 'manager' | 'customer';
interface NpcDef {
  kind: NpcKind;
  start: [number, number];        // spawn / idle position
  faceZ?: 1 | -1;                 // fixed facing for stationary staff (+Z = toward lobby)
  route: Array<[number, number]>; // OPEN-hours waypoint loop ([] or 1 pt = stationary)
  speed: number;
  tint: string;                   // body color so each role reads at a glance
}

// Tellers stand behind the counter line (z≈5.6, staff side) facing the lobby.
// The manager paces the officer platform on the staff side. Customers wander the
// public lobby. All waypoints sit in open floor, clear of fixtures and pillars.
const NPCS: NpcDef[] = [
  { kind: 'teller', start: [-3, 5.6], faceZ: 1, route: [], speed: 0, tint: '#3f6fb2' },
  { kind: 'teller', start: [3, 5.6], faceZ: 1, route: [], speed: 0, tint: '#3f6fb2' },
  { kind: 'manager', start: [-2.5, 2.2], route: [[-2.5, 2.2], [-1.8, 3.8], [-3, 4.2], [-4.2, 1.6]], speed: 1.7, tint: '#2e8d63' },
  { kind: 'customer', start: [-2, 9], route: [[-2, 9], [2, 8.2], [1, 11], [-3, 10.2]], speed: 1.5, tint: '#c98a3f' },
  { kind: 'customer', start: [3, 7.6], route: [[3, 7.6], [-1, 9.4], [-3, 11], [2, 10.6]], speed: 1.4, tint: '#b85c8a' },
  { kind: 'customer', start: [0, 10.6], route: [[0, 10.6], [-2.4, 7.9], [2.6, 9], [-1, 11.6]], speed: 1.6, tint: '#7a6cc9' },
];

type NpcMode = 'routine' | 'leaving' | 'gone';

// Each civilian/staffer runs a Hitman-style alert ladder. Suspicion is a meter
// that fills while they see the thief doing something they shouldn't (behind the
// counter, in the vault, or carrying the loot bag) and drains when they don't.
type AlertState = 'unaware' | 'suspicious' | 'searching' | 'hostile';
const NPC_VIEW_RANGE = 5.0;
const NPC_HALF_FOV = THREE.MathUtils.degToRad(32);
const NPC_PROX = 1.3;          // someone right next to you gets noticed regardless of facing
const SUS_RISE = 26;           // per second while witnessing a violation
const SUS_DECAY = 22;          // per second while nothing's wrong
const SUS_SUSPICIOUS = 25;     // pause & stare
const SUS_SEARCH = 55;         // move toward last-seen spot
const SUS_HOSTILE = 85;        // raise the alarm
const NPC_ALARM_RISE = 34;     // a hostile witness drives the global alarm this fast
// Civilians can't tackle you — a panicking witness trips the SILENT alarm (police
// called, response clock running) but can't slam the bank into instant lockdown.
// That's a guard's job. So a quick smash-and-grab under a teller's nose is
// survivable; loitering in a guard's cone is not.
const NPC_ALARM_CEIL = 70;     // witness-driven alarm tops out here (> ALARM_TRIP, < ALARM_MAX)

function alertFromSus(sus: number): AlertState {
  if (sus >= SUS_HOSTILE) return 'hostile';
  if (sus >= SUS_SEARCH) return 'searching';
  if (sus >= SUS_SUSPICIOUS) return 'suspicious';
  return 'unaware';
}

interface NpcRuntime {
  pos: THREE.Vector3;
  facing: THREE.Vector3;
  seg: number;       // current route waypoint index
  mode: NpcMode;
  moving: boolean;   // drives the walk-bob
  bob: number;       // accumulated walk phase
  sus: number;       // 0..100 suspicion meter
  alert: AlertState;
  lastSeen: THREE.Vector3 | null; // where the thief was last spotted (search target)
}

// ── Security zones — front-of-house vs back-of-house vs the vault ─────────────
// The teller line at z≈6 is the public/staff boundary; the vault wall at z≈-6.3
// fences off the restricted vault room. Where the thief stands matters as much as
// what they do: behind the counter during business hours is trespassing.
type Zone = 'public' | 'staff' | 'restricted';
function zoneAt(_x: number, z: number): Zone {
  if (z < -6.3) return 'restricted';
  if (z < 6.0) return 'staff';
  return 'public';
}
// Is the thief doing something a bystander would report?
function isTrespassing(zone: Zone, phase: DayPhase, loot: boolean): boolean {
  if (loot) return true;                 // caught with the bag — always damning
  if (phase === 'afterHours') return true; // the bank is closed; nobody belongs here
  return zone !== 'public';              // wrong side of the counter during business hours
}

const EXIT_POINT: [number, number] = [0, 14]; // doors — where leaving NPCs head

function freshNpcs(): NpcRuntime[] {
  return NPCS.map((d) => ({
    pos: new THREE.Vector3(d.start[0], 0, d.start[1]),
    facing: new THREE.Vector3(0, 0, d.faceZ ?? -1),
    seg: 0,
    mode: 'routine',
    moving: false,
    bob: 0,
    sus: 0,
    alert: 'unaware',
    lastSeen: null,
  }));
}

// Does this NPC currently see the thief? Same cone/range/occlusion model as a
// guard, but gentler reach — a bystander, not a trained sentry.
function npcSeesPlayer(n: NpcRuntime, player: THREE.Vector3): boolean {
  const dx = player.x - n.pos.x, dz = player.z - n.pos.z;
  const dist = Math.hypot(dx, dz);
  if (dist < NPC_PROX) return true;
  if (dist > NPC_VIEW_RANGE) return false;
  const nx = dx / dist, nz = dz / dist;
  if (nx * n.facing.x + nz * n.facing.z < Math.cos(NPC_HALF_FOV)) return false;
  if (rayBlocked(player, n.pos)) return false;
  return true;
}

// Advance one NPC: sense the thief, update the suspicion meter + alert state, and
// either react (stare / investigate / raise the alarm) or run the daily routine.
// Deterministic: position is a pure function of fixed routes + elapsed sim time.
// Customers + manager leave at CLOSING; tellers hold the line until AFTER-HOURS.
function updateNpc(
  n: NpcRuntime, d: NpcDef, dt: number, phase: DayPhase,
  player: THREE.Vector3, trespass: boolean,
): void {
  if (n.mode === 'gone') { n.moving = false; n.alert = 'unaware'; n.sus = 0; return; }

  // ── Senses → suspicion → alert ladder ──
  const sees = trespass && npcSeesPlayer(n, player);
  if (sees) {
    if (!n.lastSeen) n.lastSeen = new THREE.Vector3();
    n.lastSeen.set(player.x, 0, player.z);
  }
  n.sus = THREE.MathUtils.clamp(n.sus + (sees ? SUS_RISE : -SUS_DECAY) * dt, 0, 100);
  n.alert = alertFromSus(n.sus);

  // ── Alerted: react instead of working ──
  if (n.alert !== 'unaware') {
    const tx = n.lastSeen?.x ?? player.x, tz = n.lastSeen?.z ?? player.z;
    const dx = tx - n.pos.x, dz = tz - n.pos.z, dd = Math.hypot(dx, dz) || 1;
    n.facing.set(dx / dd, 0, dz / dd); // turn to face the disturbance
    if (n.alert === 'searching' && d.route.length >= 2 && dd > 0.6) {
      const sp = d.speed * dt;       // mobile NPCs go investigate the last-seen spot
      n.pos.x += (dx / dd) * sp;
      n.pos.z += (dz / dd) * sp;
      for (const b of BOXES) [n.pos.x, n.pos.z] = pushOutOfBox(n.pos.x, n.pos.z, b, 0.34);
      n.moving = true;
      n.bob += dt * 8;
    } else {
      n.moving = false;             // freeze and stare (suspicious) or shout (hostile)
    }
    return;
  }

  const shouldLeave =
    phase === 'afterHours' ||
    ((d.kind === 'manager' || d.kind === 'customer') && phase === 'closing');
  if (shouldLeave) n.mode = 'leaving';

  if (n.mode === 'leaving') {
    if (n.pos.z >= EXIT_POINT[1] - 1.5) { n.mode = 'gone'; n.moving = false; return; }
    const dx = EXIT_POINT[0] - n.pos.x, dz = EXIT_POINT[1] - n.pos.z;
    const dd = Math.hypot(dx, dz) || 1;
    const sp = (d.speed || 1.6) * 1.15 * dt;
    n.pos.x += (dx / dd) * sp;
    n.pos.z += (dz / dd) * sp;
    n.facing.set(dx / dd, 0, dz / dd);
    n.moving = true;
    n.bob += dt * 8;
    return;
  }

  // routine loop
  if (d.route.length < 2) { n.moving = false; return; } // stationary (tellers)
  const tgt = d.route[n.seg];
  const dx = tgt[0] - n.pos.x, dz = tgt[1] - n.pos.z;
  const dd = Math.hypot(dx, dz);
  if (dd < 0.15) {
    n.seg = (n.seg + 1) % d.route.length;
    n.moving = false;
  } else {
    const sp = d.speed * dt;
    n.pos.x += (dx / dd) * sp;
    n.pos.z += (dz / dd) * sp;
    n.facing.set(dx / dd, 0, dz / dd);
    n.moving = true;
    n.bob += dt * 8;
  }
}

// Circle-vs-AABB push-out: shove (px,pz) of radius r to the nearest face.
function pushOutOfBox(px: number, pz: number, b: BoxObs, r: number): [number, number] {
  const minx = b.cx - b.hx - r, maxx = b.cx + b.hx + r;
  const minz = b.cz - b.hz - r, maxz = b.cz + b.hz + r;
  if (px <= minx || px >= maxx || pz <= minz || pz >= maxz) return [px, pz];
  const dl = px - minx, dr = maxx - px, dt = pz - minz, db = maxz - pz;
  const m = Math.min(dl, dr, dt, db);
  if (m === dl) return [minx, pz];
  if (m === dr) return [maxx, pz];
  if (m === dt) return [px, minz];
  return [px, maxz];
}

// Liang–Barsky: does segment (x0,z0)->(x1,z1) cross box b?
function segHitsBox(x0: number, z0: number, x1: number, z1: number, b: BoxObs): boolean {
  const dx = x1 - x0, dz = z1 - z0;
  let t0 = 0, t1 = 1;
  const p = [-dx, dx, -dz, dz];
  const q = [x0 - (b.cx - b.hx), (b.cx + b.hx) - x0, z0 - (b.cz - b.hz), (b.cz + b.hz) - z0];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) { if (q[i] < 0) return false; }
    else {
      const t = q[i] / p[i];
      if (p[i] < 0) { if (t > t1) return false; if (t > t0) t0 = t; }
      else { if (t < t0) return false; if (t < t1) t1 = t; }
    }
  }
  return t0 < t1;
}

// ─── Tap-to-move navigation: coarse grid + A* + string-pull smoothing ─────────
// A blocked-cell grid spans the arena; we A* through free cells and then greedily
// smooth the result into long straight segments the thief can walk directly.
const NAV_CELL = 0.7;                              // grid resolution (world units)
const NAV_PAD = PLAYER_R + 0.05;                   // inflate obstacles by the thief radius
const NAV_N = Math.ceil((ARENA * 2) / NAV_CELL) + 1; // cells per axis
const navIdx = (gx: number, gz: number) => gz * NAV_N + gx;
const cellToWorld = (gx: number, gz: number): [number, number] => [
  -ARENA + gx * NAV_CELL, -ARENA + gz * NAV_CELL,
];
const worldToCell = (x: number, z: number): [number, number] => [
  Math.round((x + ARENA) / NAV_CELL), Math.round((z + ARENA) / NAV_CELL),
];

// Is the world point (x,z) inside any inflated obstacle (so the thief can't stand there)?
function pointBlocked(x: number, z: number): boolean {
  if (x < -ARENA || x > ARENA || z < -ARENA || z > ARENA) return true;
  for (const [px, pz] of PILLARS) {
    if (Math.hypot(x - px, z - pz) < PILLAR_R + NAV_PAD) return true;
  }
  if (Math.hypot(x - VAULT_POS.x, z - VAULT_POS.z) < VAULT_COLLIDE) return true;
  for (const b of BOXES) {
    if (x > b.cx - b.hx - NAV_PAD && x < b.cx + b.hx + NAV_PAD &&
        z > b.cz - b.hz - NAV_PAD && z < b.cz + b.hz + NAV_PAD) return true;
  }
  return false;
}

// Precompute the blocked grid once at module load.
const NAV_BLOCKED: boolean[] = (() => {
  const arr = new Array(NAV_N * NAV_N).fill(false);
  for (let gz = 0; gz < NAV_N; gz++) {
    for (let gx = 0; gx < NAV_N; gx++) {
      const [wx, wz] = cellToWorld(gx, gz);
      arr[navIdx(gx, gz)] = pointBlocked(wx, wz);
    }
  }
  return arr;
})();

const navFree = (gx: number, gz: number) =>
  gx >= 0 && gx < NAV_N && gz >= 0 && gz < NAV_N && !NAV_BLOCKED[navIdx(gx, gz)];

// Nearest free cell to (gx,gz) via expanding-ring search (for taps that land on
// a wall or just off the floor — snap to the closest standable cell).
function nearestFree(gx: number, gz: number): [number, number] | null {
  if (navFree(gx, gz)) return [gx, gz];
  for (let r = 1; r < NAV_N; r++) {
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
        if (navFree(gx + dx, gz + dz)) return [gx + dx, gz + dz];
      }
    }
  }
  return null;
}

// Is the straight world segment (x0,z0)->(x1,z1) clear of every obstacle?
// Sampled finely enough that we never tunnel through a thin wall.
function lineClear(x0: number, z0: number, x1: number, z1: number): boolean {
  const steps = Math.ceil(Math.hypot(x1 - x0, z1 - z0) / (NAV_CELL * 0.5));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (pointBlocked(x0 + (x1 - x0) * t, z0 + (z1 - z0) * t)) return false;
  }
  return true;
}

// A* from world (sx,sz) to (tx,tz); returns smoothed world waypoints (excluding
// the start, ending at the goal) or null if unreachable.
function findPath(sx: number, sz: number, tx: number, tz: number): { x: number; z: number }[] | null {
  const start = nearestFree(...worldToCell(sx, sz));
  const goal = nearestFree(...worldToCell(tx, tz));
  if (!start || !goal) return null;
  const [sgx, sgz] = start, [tgx, tgz] = goal;
  if (sgx === tgx && sgz === tgz) return [{ x: tx, z: tz }];

  const h = (gx: number, gz: number) => Math.hypot(gx - tgx, gz - tgz);
  const open: number[] = [navIdx(sgx, sgz)];
  const came = new Map<number, number>();
  const gScore = new Map<number, number>([[navIdx(sgx, sgz), 0]]);
  const fScore = new Map<number, number>([[navIdx(sgx, sgz), h(sgx, sgz)]]);
  const closed = new Set<number>();
  const SQRT2 = Math.SQRT2;
  let found = false;

  while (open.length) {
    // pop lowest fScore
    let bi = 0;
    for (let i = 1; i < open.length; i++) {
      if ((fScore.get(open[i]) ?? Infinity) < (fScore.get(open[bi]) ?? Infinity)) bi = i;
    }
    const cur = open.splice(bi, 1)[0];
    if (cur === navIdx(tgx, tgz)) { found = true; break; }
    closed.add(cur);
    const cx = cur % NAV_N, cz = Math.floor(cur / NAV_N);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dz) continue;
        const nx = cx + dx, nz = cz + dz;
        if (!navFree(nx, nz)) continue;
        // no corner-cutting: a diagonal needs both orthogonal neighbors free
        if (dx && dz && (!navFree(cx + dx, cz) || !navFree(cx, cz + dz))) continue;
        const ni = navIdx(nx, nz);
        if (closed.has(ni)) continue;
        const tentative = (gScore.get(cur) ?? Infinity) + (dx && dz ? SQRT2 : 1);
        if (tentative < (gScore.get(ni) ?? Infinity)) {
          came.set(ni, cur);
          gScore.set(ni, tentative);
          fScore.set(ni, tentative + h(nx, nz));
          if (!open.includes(ni)) open.push(ni);
        }
      }
    }
  }
  if (!found) return null;

  // reconstruct cell path
  const cells: number[] = [];
  let c: number | undefined = navIdx(tgx, tgz);
  while (c !== undefined) { cells.unshift(c); c = came.get(c); }

  // string-pull: greedily skip to the farthest directly-visible waypoint
  const pts = cells.map((ci) => {
    const [wx, wz] = cellToWorld(ci % NAV_N, Math.floor(ci / NAV_N));
    return { x: wx, z: wz };
  });
  const out: { x: number; z: number }[] = [];
  let anchor = { x: sx, z: sz };
  let i = 0;
  while (i < pts.length) {
    let far = i;
    for (let j = pts.length - 1; j > i; j--) {
      if (lineClear(anchor.x, anchor.z, pts[j].x, pts[j].z)) { far = j; break; }
    }
    out.push(pts[far]);
    anchor = pts[far];
    i = far + 1;
  }
  // land on the actual tapped point if we can reach it from the last waypoint
  if (out.length && lineClear(anchor.x, anchor.z, tx, tz) && !pointBlocked(tx, tz)) {
    out[out.length - 1] = { x: tx, z: tz };
  }
  return out;
}

// ─── Shared mutable game state (lives outside React render) ──────────────────
interface GameState {
  phase: Phase;
  alarm: number;
  timeLeft: number;
  loot: boolean;
  crackProgress: number;
  player: THREE.Vector3;
  guardPos: THREE.Vector3[];
  guardFacing: THREE.Vector3[];
  guardT: number[];        // 0..1 along A→B
  guardDir: number[];      // +1 / -1
  seen: boolean;
  started: boolean;
  path: { x: number; z: number }[] | null;  // tap-to-move waypoints (null = idle)
  pathIdx: number;
  clock: number;             // game-minutes since midnight
  dayPhase: DayPhase;
  npcs: NpcRuntime[];
  hasKey: boolean;           // lifted the vault key from the manager's desk
  policeCalled: boolean;     // silent alarm tripped → response clock running
  police: number;            // seconds until police arrive
}

function freshState(): GameState {
  return {
    phase: 'ready',
    alarm: 0,
    timeLeft: TIME_LIMIT,
    loot: false,
    crackProgress: 0,
    player: SPAWN.clone(),
    guardPos: GUARDS.map((g) => new THREE.Vector3(g.a[0], 0, g.a[1])),
    guardFacing: GUARDS.map(() => new THREE.Vector3(1, 0, 0)),
    guardT: GUARDS.map((g) => g.startT),
    guardDir: GUARDS.map((g) => g.dir),
    seen: false,
    started: false,
    path: null,
    pathIdx: 0,
    clock: CLOCK_START,
    dayPhase: 'open',
    npcs: freshNpcs(),
    hasKey: false,
    policeCalled: false,
    police: POLICE_TIME,
  };
}

declare global {
  interface Window {
    __heistState?: {
      phase: Phase; alarm: number; timeLeft: number;
      loot: boolean; won: boolean; caught: boolean; started: boolean;
      px?: number; pz?: number;
      clock?: number; dayPhase?: DayPhase;
      suspicion?: number; witnesses?: number;
      hasKey?: boolean; policeCalled?: boolean; police?: number;
      alarmTier?: AlarmTier;
    };
    __heist?: {
      input: HeistInput;
      start: () => void;
      reset: () => void;
      moveTo: (x: number, z: number) => void;
    };
  }
}

// ─── Geometry helpers ────────────────────────────────────────────────────────

// Flat circular-sector ("vision cone") lying on the floor, apex at origin,
// opening toward -Z (local forward). Rotated to face the guard's heading.
function useConeGeometry() {
  return useMemo(() => {
    const segs = 24;
    const geo = new THREE.BufferGeometry();
    const verts: number[] = [];
    for (let i = 0; i < segs; i++) {
      const a0 = -HALF_FOV + (2 * HALF_FOV * i) / segs;
      const a1 = -HALF_FOV + (2 * HALF_FOV * (i + 1)) / segs;
      verts.push(0, 0.02, 0);
      verts.push(Math.sin(a0) * VIEW_RANGE, 0.02, -Math.cos(a0) * VIEW_RANGE);
      verts.push(Math.sin(a1) * VIEW_RANGE, 0.02, -Math.cos(a1) * VIEW_RANGE);
    }
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    geo.computeVertexNormals();
    return geo;
  }, []);
}

// ─── Detection math ──────────────────────────────────────────────────────────

function rayBlocked(from: THREE.Vector3, to: THREE.Vector3): boolean {
  // Player→guard segment blocked by any pillar circle?
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len2 = dx * dx + dz * dz;
  for (const [px, pz] of PILLARS) {
    const t = len2 === 0 ? 0 : ((px - from.x) * dx + (pz - from.z) * dz) / len2;
    if (t <= 0 || t >= 1) continue; // pillar not between the two points
    const cx = from.x + t * dx;
    const cz = from.z + t * dz;
    const d2 = (px - cx) ** 2 + (pz - cz) ** 2;
    if (d2 < PILLAR_R * PILLAR_R) return true;
  }
  for (const b of BOXES) {
    if (b.occludes && segHitsBox(from.x, from.z, to.x, to.z, b)) return true;
  }
  return false;
}

function guardSeesPlayer(gp: THREE.Vector3, facing: THREE.Vector3, player: THREE.Vector3): boolean {
  const to = new THREE.Vector3().subVectors(player, gp);
  to.y = 0;
  const dist = to.length();
  if (dist < PROXIMITY_BUST) return true;        // bumped right into them
  if (dist > VIEW_RANGE) return false;
  to.normalize();
  const cosAng = to.dot(facing);
  if (cosAng < Math.cos(HALF_FOV)) return false; // outside the cone angle
  if (rayBlocked(player, gp)) return false;       // a pillar breaks line of sight
  return true;
}

// Checkerboard marble floor — the classic bank-lobby look, and a light floor
// keeps the moving thief readable from the top-down camera.
function useMarbleTexture() {
  return useMemo(() => {
    const N = 8, px = 1024, cell = px / N;
    const cv = document.createElement('canvas');
    cv.width = cv.height = px;
    const ctx = cv.getContext('2d')!;
    for (let r = 0; r < N; r++) {
      for (let c = 0; c < N; c++) {
        const light = (r + c) % 2 === 0;
        ctx.fillStyle = light ? '#d7d1c2' : '#b9b2a0';
        ctx.fillRect(c * cell, r * cell, cell, cell);
        // faint veining for a polished-stone feel
        ctx.strokeStyle = light ? 'rgba(150,150,140,0.18)' : 'rgba(90,88,78,0.22)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(c * cell + cell * 0.15, r * cell + cell * 0.8);
        ctx.bezierCurveTo(c * cell + cell * 0.4, r * cell + cell * 0.3, c * cell + cell * 0.6, r * cell + cell * 0.7, c * cell + cell * 0.9, r * cell + cell * 0.2);
        ctx.stroke();
      }
    }
    // grout lines
    ctx.strokeStyle = 'rgba(60,58,52,0.35)';
    ctx.lineWidth = 3;
    for (let i = 0; i <= N; i++) {
      ctx.beginPath(); ctx.moveTo(i * cell, 0); ctx.lineTo(i * cell, px); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i * cell); ctx.lineTo(px, i * cell); ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    return tex;
  }, []);
}

// ─── Decorative props (purely visual — NEVER added to nav/collision/occlusion,
//     so the pathfinding grid, guard sightlines, and E2E routes are untouched) ──

// Potted ficus — corners, doorways, lounge. Reads as greenery from above.
function Plant({ position, scale = 1 }: { position: [number, number, number]; scale?: number }) {
  const blobs: [number, number, number, number][] = [
    [0, 1.0, 0, 0.55], [0.24, 1.32, 0.12, 0.42], [-0.22, 1.38, -0.14, 0.4],
    [0.1, 1.62, -0.05, 0.4], [-0.05, 1.85, 0.08, 0.32],
  ];
  return (
    <group position={position} scale={scale}>
      <mesh position={[0, 0.26, 0]} castShadow>
        <cylinderGeometry args={[0.3, 0.24, 0.52, 18]} />
        <meshStandardMaterial color="#cfc4ad" metalness={0.1} roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.52, 0]}>
        <cylinderGeometry args={[0.28, 0.28, 0.06, 18]} />
        <meshStandardMaterial color="#241d16" roughness={1} />
      </mesh>
      {blobs.map(([x, y, z, r], i) => (
        <mesh key={i} position={[x, y, z]} castShadow>
          <icosahedronGeometry args={[r, 1]} />
          <meshStandardMaterial color={i % 2 ? '#3f7d3a' : '#326230'} roughness={0.95} flatShading />
        </mesh>
      ))}
    </group>
  );
}

// Brass queue post; QueueLine strings velvet rope between a run of them.
function Stanchion({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.025, 0]}>
        <cylinderGeometry args={[0.19, 0.22, 0.05, 18]} />
        <meshStandardMaterial color="#2f2f35" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.48, 0]} castShadow>
        <cylinderGeometry args={[0.035, 0.035, 0.9, 12]} />
        <meshStandardMaterial color="#c9a24a" metalness={0.85} roughness={0.25} />
      </mesh>
      <mesh position={[0, 0.96, 0]}>
        <sphereGeometry args={[0.075, 14, 14]} />
        <meshStandardMaterial color="#d8b24f" metalness={0.9} roughness={0.2} />
      </mesh>
    </group>
  );
}

function QueueLine({ pts }: { pts: [number, number][] }) {
  return (
    <group>
      {pts.map((p, i) => <Stanchion key={i} position={[p[0], 0, p[1]]} />)}
      {pts.slice(1).map((b, i) => {
        const a = pts[i];
        const mx = (a[0] + b[0]) / 2, mz = (a[1] + b[1]) / 2;
        const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
        const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
        return (
          <mesh key={`r${i}`} position={[mx, 0.74, mz]} rotation={[0, -ang, 0]} castShadow>
            <boxGeometry args={[len, 0.05, 0.05]} />
            <meshStandardMaterial color="#7a1f2b" roughness={0.65} />
          </mesh>
        );
      })}
    </group>
  );
}

// Waiting-lounge sofa (rotation in radians; faces +Z at 0).
function Sofa({ position, rotation = 0 }: { position: [number, number, number]; rotation?: number }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.24, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.9, 0.32, 0.78]} />
        <meshStandardMaterial color="#37506b" roughness={0.85} />
      </mesh>
      {[-0.46, 0.46].map((x, i) => (
        <mesh key={i} position={[x, 0.46, 0.04]} castShadow>
          <boxGeometry args={[0.82, 0.26, 0.66]} />
          <meshStandardMaterial color="#425e7d" roughness={0.8} />
        </mesh>
      ))}
      <mesh position={[0, 0.6, -0.3]} castShadow>
        <boxGeometry args={[1.9, 0.62, 0.18]} />
        <meshStandardMaterial color="#37506b" roughness={0.85} />
      </mesh>
      {[-0.95, 0.95].map((x, i) => (
        <mesh key={`a${i}`} position={[x, 0.46, 0]} castShadow>
          <boxGeometry args={[0.18, 0.5, 0.78]} />
          <meshStandardMaterial color="#2e4357" roughness={0.85} />
        </mesh>
      ))}
    </group>
  );
}

function CoffeeTable({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[1.1, 0.08, 0.6]} />
        <meshStandardMaterial color="#2b2b30" metalness={0.3} roughness={0.4} />
      </mesh>
      {[[-0.48, -0.24], [0.48, -0.24], [-0.48, 0.24], [0.48, 0.24]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.15, z]}>
          <cylinderGeometry args={[0.03, 0.03, 0.3, 8]} />
          <meshStandardMaterial color="#c9a24a" metalness={0.8} roughness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

// Monitor + office chair behind a desk so it reads as a real workstation.
// `face` (radians) points the chair/monitor toward the customer side.
function DeskKit({ position, face = 0 }: { position: [number, number, number]; face?: number }) {
  return (
    <group position={position} rotation={[0, face, 0]}>
      {/* monitor on the desktop, screen toward +Z (customer side) */}
      <mesh position={[0, 1.0, 0.18]} castShadow>
        <boxGeometry args={[0.5, 0.34, 0.05]} />
        <meshStandardMaterial color="#15171c" metalness={0.3} roughness={0.4} />
      </mesh>
      <mesh position={[0, 1.0, 0.21]}>
        <planeGeometry args={[0.44, 0.28]} />
        <meshStandardMaterial color="#0c2a4a" emissive="#2e7bd6" emissiveIntensity={0.5} />
      </mesh>
      <mesh position={[0, 0.84, 0.18]}>
        <boxGeometry args={[0.12, 0.08, 0.12]} />
        <meshStandardMaterial color="#23252b" metalness={0.3} roughness={0.5} />
      </mesh>
      {/* chair on the staff side (-Z) */}
      <group position={[0, 0, -0.85]}>
        <mesh position={[0, 0.5, 0]} castShadow>
          <boxGeometry args={[0.5, 0.1, 0.5]} />
          <meshStandardMaterial color="#1c1f26" roughness={0.7} />
        </mesh>
        <mesh position={[0, 0.82, -0.22]} castShadow>
          <boxGeometry args={[0.5, 0.6, 0.1]} />
          <meshStandardMaterial color="#1c1f26" roughness={0.7} />
        </mesh>
        <mesh position={[0, 0.26, 0]}>
          <cylinderGeometry args={[0.04, 0.04, 0.46, 10]} />
          <meshStandardMaterial color="#3a3a3f" metalness={0.5} roughness={0.4} />
        </mesh>
      </group>
    </group>
  );
}

// Framed artwork flush to a perimeter wall. `rotation` aims it into the room.
function WallArt({ position, rotation = 0, w = 1.4, h = 1.0, art = '#9a6b3f' }: {
  position: [number, number, number]; rotation?: number; w?: number; h?: number; art?: string;
}) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh castShadow>
        <boxGeometry args={[w + 0.16, h + 0.16, 0.08]} />
        <meshStandardMaterial color="#5a4326" metalness={0.4} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0, 0.05]}>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial color={art} roughness={0.8} />
      </mesh>
    </group>
  );
}

// Round wall clock, hands frozen near the bank's lunch-hour open.
function WallClock({ position, rotation = 0 }: { position: [number, number, number]; rotation?: number }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh>
        <cylinderGeometry args={[0.46, 0.46, 0.08, 28]} />
        <meshStandardMaterial color="#1b1b22" metalness={0.4} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0, 0.05]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.4, 0.4, 0.02, 28]} />
        <meshStandardMaterial color="#f4efe4" roughness={0.7} />
      </mesh>
      <mesh position={[0, 0.12, 0.07]}>
        <boxGeometry args={[0.04, 0.24, 0.02]} />
        <meshStandardMaterial color="#1b1b22" />
      </mesh>
      <mesh position={[0.12, 0, 0.07]} rotation={[0, 0, Math.PI / 2]}>
        <boxGeometry args={[0.03, 0.3, 0.02]} />
        <meshStandardMaterial color="#1b1b22" />
      </mesh>
    </group>
  );
}

// Ceiling-mounted security camera in the restricted areas.
function SecCam({ position, rotation = 0 }: { position: [number, number, number]; rotation?: number }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh>
        <cylinderGeometry args={[0.05, 0.05, 0.3, 8]} />
        <meshStandardMaterial color="#2a2a30" metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh position={[0, -0.18, 0.12]} rotation={[0.5, 0, 0]} castShadow>
        <boxGeometry args={[0.22, 0.18, 0.4]} />
        <meshStandardMaterial color="#33343b" metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0, -0.26, 0.32]} rotation={[0.5, 0, 0]}>
        <cylinderGeometry args={[0.07, 0.07, 0.06, 14]} />
        <meshStandardMaterial color="#0a0a0c" metalness={0.2} roughness={0.2} emissive="#ff2b2b" emissiveIntensity={0.5} />
      </mesh>
    </group>
  );
}

// Stack of gold bars on a pallet — what's actually inside the vault room.
function GoldPallet({ position }: { position: [number, number, number] }) {
  const bars: [number, number, number][] = [];
  for (let row = 0; row < 3; row++) {
    const n = 3 - (row % 2 ? 1 : 0);
    for (let i = 0; i < n; i++) {
      bars.push([(i - (n - 1) / 2) * 0.34, 0.18 + row * 0.13, (row % 2) * 0.12]);
    }
  }
  return (
    <group position={position}>
      <mesh position={[0, 0.06, 0]} castShadow>
        <boxGeometry args={[1.3, 0.12, 0.8]} />
        <meshStandardMaterial color="#5b4632" roughness={0.8} />
      </mesh>
      {bars.map((p, i) => (
        <mesh key={i} position={p} castShadow>
          <boxGeometry args={[0.3, 0.12, 0.16]} />
          <meshStandardMaterial color="#e9c34a" metalness={0.95} roughness={0.25} emissive="#7a5e12" emissiveIntensity={0.25} />
        </mesh>
      ))}
    </group>
  );
}

// Banded cash bundles stacked on a cart.
function CashStack({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      {[[-0.2, 0.12, 0], [0.2, 0.12, 0], [0, 0.12, 0.22], [-0.1, 0.28, 0.1], [0.18, 0.28, -0.05]].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.34, 0.14, 0.2]} />
          <meshStandardMaterial color={i % 2 ? '#6f9b6a' : '#7faa78'} roughness={0.85} />
        </mesh>
      ))}
    </group>
  );
}

// Hanging pendant lamp (visual depth; the real lighting is the rig above).
function Pendant({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.6, 0]}>
        <cylinderGeometry args={[0.015, 0.015, 1.2, 6]} />
        <meshStandardMaterial color="#1b1b22" />
      </mesh>
      <mesh position={[0, 0, 0]}>
        <coneGeometry args={[0.3, 0.34, 18, 1, true]} />
        <meshStandardMaterial color="#2b2b30" metalness={0.5} roughness={0.4} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, -0.05, 0]}>
        <sphereGeometry args={[0.13, 14, 14]} />
        <meshStandardMaterial color="#fff3d6" emissive="#ffdf9e" emissiveIntensity={1.4} />
      </mesh>
    </group>
  );
}

// ─── The simulation + scene (runs inside the Canvas) ─────────────────────────

function HeistScene({ game }: { game: React.MutableRefObject<GameState> }) {
  const coneGeo = useConeGeometry();
  const marbleTex = useMarbleTexture();
  const playerRef = useRef<THREE.Group>(null);
  const guardRefs = useRef<(THREE.Group | null)[]>([]);
  const npcRefs = useRef<(THREE.Group | null)[]>([]);
  const npcMarkRefs = useRef<(THREE.Mesh | null)[]>([]);
  const coneMatRefs = useRef<(THREE.MeshBasicMaterial | null)[]>([]);
  const vaultMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const keyRef = useRef<THREE.Group>(null);
  const ambientRef = useRef<THREE.AmbientLight>(null);
  const dirRef = useRef<THREE.DirectionalLight>(null);
  const followRef = useRef<THREE.PointLight>(null);
  const targetRef = useRef<THREE.Mesh>(null);
  const { camera } = useThree();
  // Third-person rig: the offset/fov adapt to the viewport; the follow itself
  // runs every frame in useFrame so the camera tracks the moving thief.
  const camOffset = useRef(CAM_OFFSET.clone());
  const camReady = useRef(false);

  useEffect(() => {
    const fit = () => {
      const aspect = window.innerWidth / Math.max(1, window.innerHeight);
      const cam = camera as THREE.PerspectiveCamera;
      if (aspect < 1) {
        camOffset.current.copy(CAM_OFFSET_PORTRAIT);
        cam.fov = 62;
      } else {
        camOffset.current.copy(CAM_OFFSET);
        cam.fov = 50;
      }
      cam.updateProjectionMatrix();
      camReady.current = false; // re-snap on orientation change
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [camera]);

  useFrame((_, rawDt) => {
    const g = game.current;
    const dt = Math.min(rawDt, 0.05); // clamp huge frames (tab switches) for stability
    const input = window.__heist?.input ?? { forward: false, back: false, left: false, right: false };

    const active = g.started && (g.phase === 'infiltrate' || g.phase === 'cracking' || g.phase === 'escape');

    if (active) {
      // ── Player movement (world-axis; camera looks down -Z) ──
      // Keyboard takes priority and cancels any active tap-to-move path; with no
      // keys held we follow the A* waypoints toward the last tapped floor point.
      const kx = (input.right ? 1 : 0) - (input.left ? 1 : 0);
      const kz = (input.back ? 1 : 0) - (input.forward ? 1 : 0);
      let mx = 0, mz = 0;
      if (kx !== 0 || kz !== 0) {
        g.path = null;          // manual override
        mx = kx; mz = kz;
      } else if (g.path && g.pathIdx < g.path.length) {
        const wp = g.path[g.pathIdx];
        const dx = wp.x - g.player.x, dz = wp.z - g.player.z;
        const d = Math.hypot(dx, dz);
        if (d < 0.2) {
          g.pathIdx++;
          if (g.pathIdx >= g.path.length) g.path = null; // arrived
        } else {
          mx = dx / d; mz = dz / d;
        }
      }
      const mv = new THREE.Vector3(mx, 0, mz);
      if (mv.lengthSq() > 0) {
        if (mv.lengthSq() > 1) mv.normalize();
        mv.multiplyScalar(PLAYER_SPEED * dt);
        g.player.add(mv);
        g.player.x = THREE.MathUtils.clamp(g.player.x, -ARENA, ARENA);
        g.player.z = THREE.MathUtils.clamp(g.player.z, -ARENA, ARENA);
        // keep the thief out of pillars
        for (const [px, pz] of PILLARS) {
          const d = Math.hypot(g.player.x - px, g.player.z - pz);
          const minD = PILLAR_R + PLAYER_R;
          if (d < minD && d > 0) {
            const push = (minD - d) / d;
            g.player.x += (g.player.x - px) * push;
            g.player.z += (g.player.z - pz) * push;
          }
        }
        // keep the thief out of counters / desks / vault walls
        for (const b of BOXES) {
          [g.player.x, g.player.z] = pushOutOfBox(g.player.x, g.player.z, b, PLAYER_R);
        }
        // stop at the vault face rather than walking through it
        const dv = Math.hypot(g.player.x - VAULT_POS.x, g.player.z - VAULT_POS.z);
        if (dv < VAULT_COLLIDE && dv > 0) {
          const push = (VAULT_COLLIDE - dv) / dv;
          g.player.x += (g.player.x - VAULT_POS.x) * push;
          g.player.z += (g.player.z - VAULT_POS.z) * push;
        }
      }

      // ── Guards patrol their segments ──
      g.guardPos.forEach((gp, i) => {
        const def = GUARDS[i];
        const ax = def.a[0], az = def.a[1], bx = def.b[0], bz = def.b[1];
        const segLen = Math.hypot(bx - ax, bz - az);
        g.guardT[i] += (g.guardDir[i] * GUARD_SPEED * dt) / segLen;
        if (g.guardT[i] >= 1) { g.guardT[i] = 1; g.guardDir[i] = -1; }
        if (g.guardT[i] <= 0) { g.guardT[i] = 0; g.guardDir[i] = 1; }
        const nx = ax + (bx - ax) * g.guardT[i];
        const nz = az + (bz - az) * g.guardT[i];
        g.guardFacing[i].set(Math.sign(nx - gp.x) || g.guardFacing[i].x, 0, Math.sign(nz - gp.z) || g.guardFacing[i].z).normalize();
        gp.set(nx, 0, nz);
      });

      // ── Detection → alarm ──
      let seen = false;
      for (let i = 0; i < g.guardPos.length; i++) {
        if (guardSeesPlayer(g.guardPos[i], g.guardFacing[i], g.player)) { seen = true; break; }
      }
      g.seen = seen;
      g.alarm += (seen ? ALARM_RISE : -ALARM_DECAY) * dt;
      g.alarm = THREE.MathUtils.clamp(g.alarm, 0, ALARM_MAX);

      // ── Timer + day clock ──
      g.timeLeft = Math.max(0, g.timeLeft - dt);
      g.clock = CLOCK_START + (TIME_LIMIT - g.timeLeft) * CLOCK_RATE;
      g.dayPhase = dayPhaseAt(g.clock);

      // ── The population goes about its day, watches for trouble, and clears out
      //    as the bank closes. A hostile witness drives the alarm like a guard. ──
      const trespass = isTrespassing(zoneAt(g.player.x, g.player.z), g.dayPhase, g.loot);
      let npcAlarm = 0;
      for (let i = 0; i < g.npcs.length; i++) {
        const n = g.npcs[i];
        updateNpc(n, NPCS[i], dt, g.dayPhase, g.player, trespass);
        if (n.alert === 'hostile' && n.mode !== 'gone') npcAlarm += NPC_ALARM_RISE * dt;
      }
      if (npcAlarm > 0 && g.alarm < NPC_ALARM_CEIL) {
        g.alarm = Math.min(NPC_ALARM_CEIL, g.alarm + npcAlarm);
      }

      // ── Lift the vault key off the manager's desk ──
      if (!g.hasKey && Math.hypot(g.player.x - KEY_POS.x, g.player.z - KEY_POS.z) < KEY_GRAB_RADIUS) {
        g.hasKey = true;
      }

      // ── Vault cracking (needs the key) ──
      const distVault = g.player.distanceTo(VAULT_POS);
      if (!g.loot) {
        if (distVault < CRACK_RADIUS && g.hasKey) {
          g.phase = 'cracking';
          g.crackProgress = Math.min(CRACK_TIME, g.crackProgress + dt);
          if (g.crackProgress >= CRACK_TIME) {
            g.loot = true;
            g.phase = 'escape';
          }
        } else if (g.phase === 'cracking') {
          g.phase = 'infiltrate'; // stepped away mid-crack (or arrived without the key)
        }
      }

      // ── Silent alarm → police response clock ──
      if (!g.policeCalled && g.alarm >= ALARM_TRIP) {
        g.policeCalled = true;
        g.police = POLICE_TIME;
      }
      if (g.policeCalled) {
        g.police = Math.max(0, g.police - dt);
      }

      // ── Win / lose ──
      if (g.alarm >= ALARM_MAX || g.timeLeft <= 0 || (g.policeCalled && g.police <= 0)) {
        g.phase = 'caught';
      } else if (g.loot && g.player.z >= EXIT_Z) {
        g.phase = 'won';
      }
    }

    // ── Push transforms to meshes ──
    if (playerRef.current) playerRef.current.position.copy(g.player);
    if (followRef.current) followRef.current.position.set(g.player.x, 4.5, g.player.z);
    g.guardPos.forEach((gp, i) => {
      const grp = guardRefs.current[i];
      if (grp) {
        grp.position.copy(gp);
        const f = g.guardFacing[i];
        grp.rotation.y = Math.atan2(-f.x, -f.z);
      }
      const cm = coneMatRefs.current[i];
      if (cm) cm.color.set(g.seen ? '#ff3b3b' : '#ffae57');
    });
    // NPCs: position, heading, walk-bob, and despawn once they've left.
    g.npcs.forEach((n, i) => {
      const grp = npcRefs.current[i];
      if (!grp) return;
      grp.visible = n.mode !== 'gone';
      if (n.mode === 'gone') return;
      const bob = n.moving ? Math.abs(Math.sin(n.bob)) * 0.07 : 0;
      grp.position.set(n.pos.x, bob, n.pos.z);
      grp.rotation.y = Math.atan2(-n.facing.x, -n.facing.z);
      // Alert marker: amber "?" while suspicious/searching, red "!" when reporting.
      const mark = npcMarkRefs.current[i];
      if (mark) {
        mark.visible = n.alert !== 'unaware';
        const m = mark.material as THREE.MeshBasicMaterial;
        m.color.set(n.alert === 'hostile' ? '#ff3b3b' : '#ffcf5a');
        const s = n.alert === 'hostile' ? 1 + Math.sin(performance.now() / 90) * 0.25 : 1;
        mark.scale.set(s, s, s);
      }
    });
    if (vaultMatRef.current) {
      vaultMatRef.current.emissiveIntensity = g.loot ? 0.15 : 0.9 + Math.sin(performance.now() / 350) * 0.25;
    }
    // Floating, spinning vault key — vanishes once lifted.
    if (keyRef.current) {
      keyRef.current.visible = !g.hasKey;
      keyRef.current.rotation.y += dt * 2.2;
      keyRef.current.position.y = 1.05 + Math.sin(performance.now() / 380) * 0.12;
    }
    // Daylight fades as the bank closes; after-hours runs on dim security lighting.
    {
      const ambTgt = g.dayPhase === 'afterHours' ? 0.26 : g.dayPhase === 'closing' ? 0.58 : 0.9;
      const dirTgt = g.dayPhase === 'afterHours' ? 0.32 : g.dayPhase === 'closing' ? 0.78 : 1.1;
      const k = 1 - Math.exp(-1.5 * dt);
      if (ambientRef.current) ambientRef.current.intensity += (ambTgt - ambientRef.current.intensity) * k;
      if (dirRef.current) dirRef.current.intensity += (dirTgt - dirRef.current.intensity) * k;
    }
    // Tap-to-move target marker: a pulsing ring at the destination while walking.
    if (targetRef.current) {
      const dest = g.path && g.pathIdx < g.path.length ? g.path[g.path.length - 1] : null;
      targetRef.current.visible = !!dest;
      if (dest) {
        targetRef.current.position.set(dest.x, 0.05, dest.z);
        const s = 1 + Math.sin(performance.now() / 180) * 0.12;
        targetRef.current.scale.set(s, s, s);
      }
    }

    // ── Third-person follow camera: ease toward player+offset, look just ahead ──
    const off = camOffset.current;
    const dx = g.player.x + off.x, dy = off.y, dz = g.player.z + off.z;
    if (!camReady.current) {
      camera.position.set(dx, dy, dz);
      camReady.current = true;
    } else {
      const a = 1 - Math.exp(-CAM_LERP * dt);
      camera.position.x += (dx - camera.position.x) * a;
      camera.position.y += (dy - camera.position.y) * a;
      camera.position.z += (dz - camera.position.z) * a;
    }
    camera.lookAt(g.player.x, 1, g.player.z - CAM_LOOK_AHEAD);

    // ── Publish read-only state for HUD + E2E ──
    let maxSus = 0, witnesses = 0;
    for (const n of g.npcs) {
      if (n.mode === 'gone') continue;
      if (n.sus > maxSus) maxSus = n.sus;
      if (n.alert !== 'unaware') witnesses++;
    }
    window.__heistState = {
      phase: g.phase, alarm: g.alarm, timeLeft: g.timeLeft,
      loot: g.loot, won: g.phase === 'won', caught: g.phase === 'caught',
      started: g.started, px: g.player.x, pz: g.player.z,
      clock: g.clock, dayPhase: g.dayPhase,
      suspicion: maxSus, witnesses,
      hasKey: g.hasKey, policeCalled: g.policeCalled, police: g.police,
      alarmTier: alarmTierAt(g.alarm, g.policeCalled),
    };
  });

  const WALL_H = 3.2;
  return (
    <group>
      {/* Lighting — bright, even bank-lobby daylight so the floor reads clearly */}
      <ambientLight ref={ambientRef} intensity={0.9} color="#fdfaf2" />
      <hemisphereLight args={['#fff7e6', '#6b6450', 0.7]} />
      <directionalLight ref={dirRef} position={[8, 16, 10]} intensity={1.1} color="#fff4dd" castShadow shadow-mapSize={[2048, 2048]} shadow-camera-left={-16} shadow-camera-right={16} shadow-camera-top={16} shadow-camera-bottom={-16} />
      {/* Recessed ceiling fixtures for a lit-room feel — one per zone */}
      {[[-10, 9], [10, 9], [0, 7], [-5, 0], [5, 0], [-11, -3], [11, -3], [0, -10]].map(([x, z], i) => (
        <pointLight key={i} position={[x, 6.5, z]} intensity={0.5} distance={16} decay={2} color="#fff1d6" />
      ))}
      {/* Warm key light on the vault to draw the eye */}
      <spotLight position={[0, 8.5, -6]} target-position={[0, 0, -9]} angle={0.55} penumbra={0.7} intensity={2.6} color="#ffcf8a" castShadow distance={26} />
      {/* Soft light that follows the thief so they're always visible */}
      <pointLight ref={followRef} position={[SPAWN.x, 4.5, SPAWN.z]} intensity={0.7} distance={9} decay={2} color="#dce6ff" />

      {/* Floor — polished marble checkerboard. Tap/click a spot to walk there. */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, 0]}
        receiveShadow
        onPointerDown={(e) => {
          e.stopPropagation();
          const st = window.__heistState;
          if (!st?.started || st.won || st.caught) return;
          window.__heist?.moveTo(e.point.x, e.point.z);
        }}
      >
        <planeGeometry args={[ARENA * 2 + 2, ARENA * 2 + 2]} />
        <meshStandardMaterial map={marbleTex} metalness={0.1} roughness={0.35} />
      </mesh>
      {/* Tap-to-move destination ring */}
      <mesh ref={targetRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]} visible={false}>
        <ringGeometry args={[0.45, 0.62, 28]} />
        <meshBasicMaterial color="#37d67a" transparent opacity={0.85} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      {/* Secured vault-room floor accent (darker stone behind the vault wall) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, -10.4]} receiveShadow>
        <planeGeometry args={[ARENA * 2, 7.2]} />
        <meshStandardMaterial color="#3a3326" metalness={0.2} roughness={0.6} transparent opacity={0.92} />
      </mesh>

      {/* Outer walls — warm stone */}
      {[
        { p: [0, WALL_H / 2, -ARENA - 1] as [number, number, number], s: [ARENA * 2 + 2, WALL_H, 0.4] as [number, number, number] },
        { p: [0, WALL_H / 2, ARENA + 1] as [number, number, number], s: [ARENA * 2 + 2, WALL_H, 0.4] as [number, number, number] },
        { p: [-ARENA - 1, WALL_H / 2, 0] as [number, number, number], s: [0.4, WALL_H, ARENA * 2 + 2] as [number, number, number] },
        { p: [ARENA + 1, WALL_H / 2, 0] as [number, number, number], s: [0.4, WALL_H, ARENA * 2 + 2] as [number, number, number] },
      ].map((w, i) => (
        <mesh key={i} position={w.p} castShadow receiveShadow>
          <boxGeometry args={w.s} />
          <meshStandardMaterial color="#cdbba0" metalness={0.05} roughness={0.85} />
        </mesh>
      ))}

      {/* Bank columns — fluted marble with gold caps/bases */}
      {PILLARS.map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 1.6, 0]} castShadow receiveShadow>
            <cylinderGeometry args={[PILLAR_R, PILLAR_R, 3.2, 28]} />
            <meshStandardMaterial color="#ece7da" metalness={0.05} roughness={0.55} />
          </mesh>
          <mesh position={[0, 3.25, 0]} castShadow>
            <cylinderGeometry args={[PILLAR_R + 0.14, PILLAR_R + 0.14, 0.22, 28]} />
            <meshStandardMaterial color="#c9a24a" metalness={0.7} roughness={0.3} />
          </mesh>
          <mesh position={[0, 0.12, 0]} castShadow>
            <cylinderGeometry args={[PILLAR_R + 0.16, PILLAR_R + 0.16, 0.24, 28]} />
            <meshStandardMaterial color="#b9b2a0" metalness={0.1} roughness={0.6} />
          </mesh>
        </group>
      ))}

      {/* Bank fixtures — walls, counters, desks, ATMs, safe-deposit boxes.
          Material per kind so each room reads as what it is from above. */}
      {BOXES.map((b, i) => {
        const mat = BOX_MAT[b.kind];
        return (
          <mesh key={i} position={[b.cx, b.h / 2, b.cz]} castShadow receiveShadow>
            <boxGeometry args={[b.hx * 2, b.h, b.hz * 2]} />
            <meshStandardMaterial color={mat.color} metalness={mat.metalness} roughness={mat.roughness} />
          </mesh>
        );
      })}
      {/* Dark stone counter tops on the teller line */}
      {BOXES.filter((b) => b.kind === 'counter').map((b, i) => (
        <mesh key={`top${i}`} position={[b.cx, b.h + 0.04, b.cz]} castShadow>
          <boxGeometry args={[b.hx * 2 + 0.2, 0.08, b.hz * 2 + 0.2]} />
          <meshStandardMaterial color="#2b2b30" metalness={0.3} roughness={0.4} />
        </mesh>
      ))}
      {/* ATM screens (glowing faces toward the entrance) */}
      {BOXES.filter((b) => b.kind === 'atm').map((b, i) => (
        <mesh key={`atm${i}`} position={[b.cx, 1.05, b.cz + b.hz + 0.01]}>
          <planeGeometry args={[b.hx * 1.3, 0.7]} />
          <meshStandardMaterial color="#0c2a4a" emissive="#2e7bd6" emissiveIntensity={0.7} />
        </mesh>
      ))}

      {/* Vault — recessed steel door with a glowing dial */}
      <group position={[VAULT_POS.x, 0, VAULT_POS.z]}>
        {/* Door frame */}
        <mesh position={[0, 1.5, -0.35]} castShadow receiveShadow>
          <boxGeometry args={[3.4, 3, 0.5]} />
          <meshStandardMaterial color="#3b3f46" metalness={0.6} roughness={0.5} />
        </mesh>
        {/* Round steel door */}
        <mesh position={[0, 1.4, 0.05]} rotation={[Math.PI / 2, 0, 0]} castShadow>
          <cylinderGeometry args={[1.25, 1.25, 0.5, 48]} />
          <meshStandardMaterial color="#9aa3ad" metalness={0.92} roughness={0.28} />
        </mesh>
        {/* Bolt ring */}
        <mesh position={[0, 1.4, 0.32]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.95, 0.08, 12, 48]} />
          <meshStandardMaterial color="#6f7882" metalness={0.9} roughness={0.35} />
        </mesh>
        {/* Glowing combination dial */}
        <mesh position={[0, 1.4, 0.38]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.42, 0.42, 0.16, 32]} />
          <meshStandardMaterial ref={vaultMatRef} color="#d9a441" emissive="#ffb02e" emissiveIntensity={0.9} metalness={0.9} roughness={0.2} />
        </mesh>
        {/* Spokes on the handle */}
        {[0, 1, 2, 3].map((k) => (
          <mesh key={k} position={[0, 1.4, 0.42]} rotation={[0, 0, (k * Math.PI) / 4]}>
            <boxGeometry args={[1.4, 0.1, 0.1]} />
            <meshStandardMaterial color="#7e8790" metalness={0.85} roughness={0.35} />
          </mesh>
        ))}
      </group>

      {/* Vault key — floating, spinning gold key on the officer's desk.
          Hidden once lifted (keyRef.visible toggled in useFrame). */}
      <group ref={keyRef} position={[KEY_POS.x, 1.05, KEY_POS.z]}>
        {/* Bow (ring head) */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.16, 0.05, 10, 24]} />
          <meshStandardMaterial color="#ffcf5a" emissive="#ffb02e" emissiveIntensity={0.8} metalness={0.9} roughness={0.25} />
        </mesh>
        {/* Shaft */}
        <mesh position={[0, -0.26, 0]}>
          <cylinderGeometry args={[0.04, 0.04, 0.4, 12]} />
          <meshStandardMaterial color="#ffcf5a" emissive="#ffb02e" emissiveIntensity={0.7} metalness={0.9} roughness={0.25} />
        </mesh>
        {/* Bit (teeth) */}
        <mesh position={[0.08, -0.42, 0]}>
          <boxGeometry args={[0.16, 0.12, 0.06]} />
          <meshStandardMaterial color="#ffcf5a" emissive="#ffb02e" emissiveIntensity={0.7} metalness={0.9} roughness={0.25} />
        </mesh>
        <pointLight position={[0, 0, 0]} intensity={0.6} distance={3} decay={2} color="#ffce6b" />
      </group>

      {/* Exit zone — green runner rug + arrows back to the door */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 9.8]}>
        <planeGeometry args={[6, 2.4]} />
        <meshBasicMaterial color="#37d67a" transparent opacity={0.3} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 6.5]}>
        <planeGeometry args={[2.2, 5]} />
        <meshBasicMaterial color="#37d67a" transparent opacity={0.12} />
      </mesh>

      {/* Player — the thief (dark cat-burglar so they pop on light marble) */}
      <group ref={playerRef} position={[SPAWN.x, 0, SPAWN.z]}>
        <mesh position={[0, 0.7, 0]} castShadow>
          <capsuleGeometry args={[PLAYER_R, 0.8, 8, 16]} />
          <meshStandardMaterial color="#21314f" metalness={0.2} roughness={0.5} emissive="#3a5bd0" emissiveIntensity={0.45} />
        </mesh>
        <mesh position={[0, 1.45, 0]} castShadow>
          <sphereGeometry args={[0.32, 16, 16]} />
          <meshStandardMaterial color="#16203a" metalness={0.2} roughness={0.6} />
        </mesh>
      </group>

      {/* Guards + vision cones */}
      {GUARDS.map((_, i) => (
        <group key={i} ref={(el) => { guardRefs.current[i] = el; }}>
          <mesh position={[0, 0.7, 0]} castShadow>
            <capsuleGeometry args={[0.42, 0.8, 8, 16]} />
            <meshStandardMaterial color="#c0392b" metalness={0.2} roughness={0.5} emissive="#7a1414" emissiveIntensity={0.5} />
          </mesh>
          <mesh position={[0, 1.45, 0]} castShadow>
            <sphereGeometry args={[0.3, 16, 16]} />
            <meshStandardMaterial color="#e07a6a" roughness={0.7} />
          </mesh>
          {/* Peaked cap so heading reads at a glance */}
          <mesh position={[0, 1.7, 0]} castShadow>
            <cylinderGeometry args={[0.26, 0.3, 0.18, 16]} />
            <meshStandardMaterial color="#1b1b22" roughness={0.6} />
          </mesh>
          <mesh geometry={coneGeo}>
            <meshBasicMaterial ref={(el) => { coneMatRefs.current[i] = el; }} color="#ffae57" transparent opacity={0.26} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
        </group>
      ))}

      {/* The living population — tellers, manager, customers. Capsule "people"
          tinted by role, with a facing nub so their heading reads from above. */}
      {NPCS.map((d, i) => (
        <group key={`npc${i}`} ref={(el) => { npcRefs.current[i] = el; }} position={[d.start[0], 0, d.start[1]]}>
          <mesh position={[0, 0.62, 0]} castShadow>
            <capsuleGeometry args={[0.32, 0.62, 8, 16]} />
            <meshStandardMaterial color={d.tint} metalness={0.1} roughness={0.7} />
          </mesh>
          <mesh position={[0, 1.28, 0]} castShadow>
            <sphereGeometry args={[0.26, 16, 16]} />
            <meshStandardMaterial color="#e8c9a8" roughness={0.8} />
          </mesh>
          {/* facing nub — points where the person is looking */}
          <mesh position={[0, 0.72, -0.34]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.12, 0.22, 12]} />
            <meshStandardMaterial color={d.tint} metalness={0.2} roughness={0.6} />
          </mesh>
          {/* alert marker — floats above the head when this person notices the thief */}
          <mesh ref={(el) => { npcMarkRefs.current[i] = el; }} position={[0, 1.85, 0]} visible={false}>
            <sphereGeometry args={[0.16, 12, 12]} />
            <meshBasicMaterial color="#ffcf5a" />
          </mesh>
          {/* manager carries a tiny gold tag — the vault key (wired up in Phase C) */}
          {d.kind === 'manager' && (
            <mesh position={[0, 1.7, 0]}>
              <octahedronGeometry args={[0.14, 0]} />
              <meshStandardMaterial color="#ffcf5a" emissive="#ffb02e" emissiveIntensity={0.6} metalness={0.8} roughness={0.3} />
            </mesh>
          )}
        </group>
      ))}

      {/* ─────────────────────────────────────────────────────────────────────
          DECOR — purely visual furniture & props. None of this is in BOXES /
          PILLARS, so the nav grid, guard sightlines, and E2E paths are untouched.
         ───────────────────────────────────────────────────────────────────── */}

      {/* Lobby floor emblem — brass bank seal inlaid in the marble */}
      <group position={[0, 0.02, 11.6]} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh><ringGeometry args={[1.45, 1.78, 48]} /><meshStandardMaterial color="#b9912f" metalness={0.8} roughness={0.3} /></mesh>
        <mesh><ringGeometry args={[1.18, 1.34, 48]} /><meshStandardMaterial color="#9c7a26" metalness={0.7} roughness={0.35} /></mesh>
        <mesh><circleGeometry args={[1.16, 48]} /><meshStandardMaterial color="#243042" metalness={0.3} roughness={0.55} /></mesh>
        <mesh rotation={[0, 0, Math.PI / 10]}><circleGeometry args={[0.52, 5]} /><meshStandardMaterial color="#c9a24a" metalness={0.85} roughness={0.25} /></mesh>
      </group>

      {/* Warm area rugs in the offices + a lounge rug by the entrance */}
      {[
        { p: [-11.5, 4] as [number, number], s: [3.6, 3.4] as [number, number], c: '#6e4b3a' },
        { p: [-11.5, -3] as [number, number], s: [3.6, 3.8] as [number, number], c: '#6e4b3a' },
        { p: [-11.4, 10.5] as [number, number], s: [3.2, 2.6] as [number, number], c: '#324a63' },
        { p: [11.4, 10.5] as [number, number], s: [3.2, 2.6] as [number, number], c: '#324a63' },
      ].map((r, i) => (
        <mesh key={`rug${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[r.p[0], 0.014, r.p[1]]} receiveShadow>
          <planeGeometry args={r.s} />
          <meshStandardMaterial color={r.c} roughness={0.95} />
        </mesh>
      ))}

      {/* Caution stripe across the secured vault doorway */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, -6.6]}>
        <planeGeometry args={[3.0, 0.5]} />
        <meshStandardMaterial color="#caa12a" roughness={0.7} />
      </mesh>

      {/* Glass entrance doors set into the front wall */}
      <group position={[0, 0, ARENA + 0.78]}>
        {[-1.0, 1.0].map((x, i) => (
          <mesh key={i} position={[x, 1.35, 0]}>
            <boxGeometry args={[1.7, 2.5, 0.06]} />
            <meshStandardMaterial color="#bfe0ff" metalness={0.2} roughness={0.1} transparent opacity={0.32} />
          </mesh>
        ))}
        <mesh position={[0, 1.35, 0]}><boxGeometry args={[0.16, 2.6, 0.16]} /><meshStandardMaterial color="#c9a24a" metalness={0.85} roughness={0.3} /></mesh>
        {[-1.9, 1.9].map((x, i) => (
          <mesh key={`fr${i}`} position={[x, 1.35, 0]}><boxGeometry args={[0.16, 2.6, 0.16]} /><meshStandardMaterial color="#c9a24a" metalness={0.85} roughness={0.3} /></mesh>
        ))}
        <mesh position={[0, 2.75, 0]}><boxGeometry args={[4.0, 0.18, 0.18]} /><meshStandardMaterial color="#c9a24a" metalness={0.85} roughness={0.3} /></mesh>
      </group>

      {/* Glass teller dividers + brass rail above each counter */}
      {BOXES.filter((b) => b.kind === 'counter').map((b, i) => (
        <group key={`teller${i}`}>
          <mesh position={[b.cx, 1.55, b.cz - 0.02]}>
            <boxGeometry args={[b.hx * 2 - 0.3, 0.8, 0.03]} />
            <meshStandardMaterial color="#cfe6ff" metalness={0.2} roughness={0.1} transparent opacity={0.22} />
          </mesh>
          <mesh position={[b.cx, 1.97, b.cz - 0.02]}>
            <boxGeometry args={[b.hx * 2 - 0.2, 0.06, 0.06]} />
            <meshStandardMaterial color="#c9a24a" metalness={0.85} roughness={0.3} />
          </mesh>
          {/* teller transaction gaps — small wells in the divider */}
          {[-b.hx * 0.5, b.hx * 0.5].map((dx, k) => (
            <mesh key={k} position={[b.cx + dx, 1.25, b.cz - 0.02]}>
              <boxGeometry args={[0.7, 0.18, 0.05]} />
              <meshStandardMaterial color="#1b1b22" metalness={0.3} roughness={0.5} />
            </mesh>
          ))}
        </group>
      ))}
      {/* Brass swing-gate posts at the center teller gap */}
      {[-1.55, 1.55].map((x, i) => (
        <mesh key={`gate${i}`} position={[x, 0.6, 6.6]} castShadow>
          <cylinderGeometry args={[0.07, 0.08, 1.2, 14]} />
          <meshStandardMaterial color="#c9a24a" metalness={0.85} roughness={0.3} />
        </mesh>
      ))}

      {/* ATM keypads + card slots (screens already render on the box) */}
      {BOXES.filter((b) => b.kind === 'atm').map((b, i) => (
        <group key={`atmkit${i}`}>
          <mesh position={[b.cx, 0.62, b.cz + b.hz + 0.02]}>
            <boxGeometry args={[b.hx * 1.1, 0.5, 0.06]} />
            <meshStandardMaterial color="#222631" metalness={0.4} roughness={0.5} />
          </mesh>
          <mesh position={[b.cx, 0.86, b.cz + b.hz + 0.05]}>
            <boxGeometry args={[0.5, 0.04, 0.04]} />
            <meshStandardMaterial color="#14304f" emissive="#2e7bd6" emissiveIntensity={0.5} />
          </mesh>
        </group>
      ))}

      {/* Safe-deposit door grids (right room face -X; vault room face +Z) */}
      {BOXES.filter((b) => b.kind === 'sdbox' && b.cx > 8).map((b, bi) => (
        <group key={`sdR${bi}`}>
          {[0.32, 0.62, 0.92].map((y, r) =>
            [-0.22, 0.22].map((dz, c) => (
              <mesh key={`${r}-${c}`} position={[b.cx - b.hx - 0.02, y, b.cz + dz]} rotation={[0, Math.PI / 2, 0]}>
                <planeGeometry args={[0.34, 0.22]} />
                <meshStandardMaterial color="#c7ccd2" metalness={0.8} roughness={0.3} />
              </mesh>
            )),
          )}
        </group>
      ))}
      {BOXES.filter((b) => b.kind === 'sdbox' && b.cz < -9).map((b, bi) => (
        <group key={`sdV${bi}`}>
          {[0.32, 0.62, 0.92].map((y, r) =>
            [-0.5, 0, 0.5].map((dx, c) => (
              <mesh key={`${r}-${c}`} position={[b.cx + dx, y, b.cz + b.hz + 0.02]}>
                <planeGeometry args={[0.34, 0.22]} />
                <meshStandardMaterial color="#c7ccd2" metalness={0.8} roughness={0.3} />
              </mesh>
            )),
          )}
        </group>
      ))}

      {/* Workstations — monitor + chair at the desks */}
      <DeskKit position={[-11.5, 0, 4.3]} face={0} />
      <DeskKit position={[-11.5, 0, -3]} face={0} />
      <DeskKit position={[4.5, 0, 3]} face={0} />
      <DeskKit position={[11.5, 0, -2]} face={Math.PI / 2} />

      {/* Waiting lounge — sofas, coffee tables, plants by the entrance */}
      <Sofa position={[-12, 0, 10.5]} rotation={Math.PI / 2} />
      <CoffeeTable position={[-10.7, 0, 10.5]} />
      <Sofa position={[12, 0, 10.5]} rotation={-Math.PI / 2} />
      <CoffeeTable position={[10.7, 0, 10.5]} />

      {/* Queue ropes guiding customers to the teller windows */}
      <QueueLine pts={[[-3.4, 9.4], [-3.4, 8.0], [-5.2, 8.0], [-5.2, 9.4]]} />
      <QueueLine pts={[[3.4, 9.4], [3.4, 8.0], [5.2, 8.0], [5.2, 9.4]]} />

      {/* Greenery — corners, doorways, office + vault-room accents */}
      {([
        [-13, 13], [13, 13], [-5.6, 12.6], [5.6, 12.6],
        [-12.8, 8.4], [12.8, 8.4],
        [-13.2, 5.6], [-13.2, -5.0],
        [13, 5.4], [13, -5.0],
        [-3.2, -6.0], [3.2, -6.0],
      ] as [number, number][]).map((p, i) => (
        <Plant key={`pl${i}`} position={[p[0], 0, p[1]]} scale={i < 4 ? 1.15 : 1} />
      ))}

      {/* Vault contents — gold pallets + cash carts behind the door */}
      <GoldPallet position={[-2.7, 0, -11.9]} />
      <GoldPallet position={[2.7, 0, -11.9]} />
      <CashStack position={[0, 0, -12.4]} />
      <CashStack position={[-1.2, 0, -10.8]} />
      <CashStack position={[1.4, 0, -10.6]} />

      {/* Velvet rope barrier framing the vault approach (clear of the spine) */}
      <QueueLine pts={[[-2.3, -7.3], [-2.3, -8.7]]} />
      <QueueLine pts={[[2.3, -7.3], [2.3, -8.7]]} />

      {/* Security cameras watching the secured areas */}
      <SecCam position={[-5.4, 2.95, -12.2]} rotation={-Math.PI / 4} />
      <SecCam position={[5.4, 2.95, -12.2]} rotation={Math.PI / 4} />
      <SecCam position={[-13.5, 2.95, 0]} rotation={Math.PI / 2} />
      <SecCam position={[13.5, 2.95, 1]} rotation={-Math.PI / 2} />

      {/* Framed artwork + a wall clock on the perimeter walls */}
      <WallArt position={[-14.7, 1.95, 10]} rotation={Math.PI / 2} w={1.6} h={1.1} art="#7a5230" />
      <WallArt position={[-14.7, 1.95, -2]} rotation={Math.PI / 2} w={1.4} h={1.0} art="#3f5a72" />
      <WallArt position={[14.7, 1.95, 8]} rotation={-Math.PI / 2} w={1.6} h={1.1} art="#5a6e44" />
      <WallArt position={[14.7, 1.95, -3]} rotation={-Math.PI / 2} w={1.4} h={1.0} art="#7a5230" />
      <WallArt position={[-9, 1.95, 14.7]} rotation={Math.PI} w={1.5} h={1.0} art="#3f5a72" />
      <WallArt position={[9, 1.95, 14.7]} rotation={Math.PI} w={1.5} h={1.0} art="#5a6e44" />
      <WallClock position={[-14.68, 2.55, 4]} rotation={Math.PI / 2} />

      {/* Pendant lamps for vertical depth */}
      <Pendant position={[-6, 4.4, 9.5]} />
      <Pendant position={[6, 4.4, 9.5]} />
      <Pendant position={[0, 4.4, 2]} />

      <ContactShadows position={[0, 0.02, 0]} opacity={0.4} scale={ARENA * 2.4} blur={2.2} far={6} />
    </group>
  );
}

// ─── HUD overlay (DOM; polls the read-only state on its own rAF) ─────────────

function Hud({ onStart, onRestart, onBack }: { onStart: () => void; onRestart: () => void; onBack: () => void }) {
  const [s, setS] = useState(() => window.__heistState ?? { phase: 'ready' as Phase, alarm: 0, timeLeft: TIME_LIMIT, loot: false, won: false, caught: false, started: false, clock: CLOCK_START, dayPhase: 'open' as DayPhase });

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      if (window.__heistState) setS({ ...window.__heistState });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const alarmPct = Math.round(s.alarm);
  const danger = alarmPct > 66 ? 'high' : alarmPct > 33 ? 'mid' : 'low';
  const dayPhase = s.dayPhase ?? 'open';
  const dayLabel = dayPhase === 'open' ? 'OPEN' : dayPhase === 'closing' ? 'CLOSING' : 'AFTER HOURS';
  const tier = s.alarmTier ?? 'clear';
  const tierLabel = tier === 'lockdown' ? 'LOCKDOWN' : tier === 'alarm' ? 'ALARM' : tier === 'suspect' ? 'SUSPECT' : 'CLEAR';
  const objective = s.loot
    ? '💰 LOOT SECURED — reach the green exit'
    : !s.hasKey
      ? "🔑 Steal the vault key from the officer's desk"
      : s.phase === 'cracking'
        ? '🔓 Cracking the vault… hold position'
        : '🗝 Key in hand — reach the glowing vault';
  const isTouch = useMemo(
    () => typeof window !== 'undefined' &&
      (window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0),
    [],
  );

  return (
    <div className="heist-hud">
      <div className="heist-topbar">
        <button className="heist-back" data-testid="heist-back" onClick={onBack}>← Exit</button>
        <div className="heist-title">THE VAULT JOB</div>
        <div className="heist-clock">
          <span className="heist-clock-time" data-testid="heist-clock">{formatClock(s.clock ?? CLOCK_START)}</span>
          <span className={`heist-dayphase ${dayPhase}`} data-testid="heist-dayphase">{dayLabel}</span>
        </div>
        <div className="heist-timer" data-testid="heist-timer">{s.timeLeft.toFixed(1)}s</div>
      </div>

      {s.started && (
        <div className="heist-meters">
          <div className="heist-meter-label" data-testid="heist-alarm-tier">ALARM · {tierLabel}</div>
          <div className="heist-alarm-track">
            <div className={`heist-alarm-fill ${danger}`} style={{ width: `${alarmPct}%` }} />
          </div>
          {s.policeCalled && (
            <div className="heist-watch hostile" data-testid="heist-police">
              🚨 Police en route — {Math.ceil(s.police ?? 0)}s to lockdown
            </div>
          )}
          {!s.policeCalled && (s.witnesses ?? 0) > 0 && (
            <div className={`heist-watch ${(s.suspicion ?? 0) >= SUS_HOSTILE ? 'hostile' : 'wary'}`} data-testid="heist-watch">
              👁 {(s.suspicion ?? 0) >= SUS_HOSTILE
                ? `${s.witnesses} raising the alarm!`
                : `${s.witnesses} ${s.witnesses === 1 ? 'person has' : 'people have'} noticed you`}
            </div>
          )}
          <div className="heist-status" data-testid="heist-objective">{objective}</div>
        </div>
      )}

      {s.phase === 'ready' && (
        <div className="heist-overlay">
          <div className="heist-card">
            <h1>The Vault Job</h1>
            <p>The bank is open and busy. Steal the manager's vault key, crack the vault, and get out before closing — or before the alarm hits 100.</p>
            <ul className="heist-controls">
              <li><b>{isTouch ? 'Tap the floor' : 'Click the floor (or WASD)'}</b> — walk there</li>
              <li>Grab the <span className="key-swatch" /> vault key off the officer's desk</li>
              <li>Stay out of the <span className="cone-swatch" /> cones & watch who's watching you</li>
              <li>Hold at the vault to crack it</li>
              <li>Carry the loot to the <span className="exit-swatch" /> exit</li>
            </ul>
            <button className="heist-cta" data-testid="heist-start" onClick={onStart}>Start Heist</button>
          </div>
        </div>
      )}

      {(s.phase === 'won' || s.phase === 'caught') && (
        <div className="heist-overlay">
          <div className={`heist-card ${s.phase}`}>
            <h1>{s.phase === 'won' ? (s.policeCalled ? 'GETAWAY' : 'CLEAN GETAWAY') : 'BUSTED'}</h1>
            <p>{s.phase === 'won'
              ? (s.policeCalled
                ? 'You cracked the vault and slipped out with the loot just ahead of the sirens.'
                : 'You walked out with the loot and nobody saw a thing.')
              : s.policeCalled && (s.police ?? 0) <= 0 ? 'The police arrived and sealed the exits.'
                : s.timeLeft <= 0 ? 'The clock ran out — guards locked the place down.'
                  : 'The alarm maxed out. Guards swarmed the floor.'}</p>
            <button className="heist-cta" data-testid="heist-restart" onClick={onRestart}>Run It Again</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Heist({ onBack }: { onBack: () => void }) {
  const game = useRef<GameState>(freshState());

  useEffect(() => {
    const input: HeistInput = { forward: false, back: false, left: false, right: false };
    const start = () => {
      game.current = freshState();
      game.current.started = true;
      game.current.phase = 'infiltrate';
    };
    const reset = () => { game.current = freshState(); };
    const moveTo = (x: number, z: number) => {
      const g = game.current;
      g.path = findPath(g.player.x, g.player.z, x, z);
      g.pathIdx = 0;
    };
    window.__heist = { input, start, reset, moveTo };
    window.__heistState = {
      phase: 'ready', alarm: 0, timeLeft: TIME_LIMIT,
      loot: false, won: false, caught: false, started: false,
    };

    const map: Record<string, 'forward' | 'back' | 'left' | 'right'> = {
      KeyW: 'forward', ArrowUp: 'forward',
      KeyS: 'back', ArrowDown: 'back',
      KeyA: 'left', ArrowLeft: 'left',
      KeyD: 'right', ArrowRight: 'right',
    };
    const down = (e: KeyboardEvent) => { const k = map[e.code]; if (k) { input[k] = true; e.preventDefault(); } };
    const up = (e: KeyboardEvent) => { const k = map[e.code]; if (k) { input[k] = false; } };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      delete window.__heist;
    };
  }, []);

  const handleStart = () => window.__heist?.start();
  const handleRestart = () => window.__heist?.start();

  return (
    <div className="heist-page">
      <Canvas
        shadows
        camera={{ position: [0, 11, 21], fov: 50 }}
        gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.1 }}
        style={{ background: '#20242f' }}
      >
        <HeistScene game={game} />
      </Canvas>
      <Hud onStart={handleStart} onRestart={handleRestart} onBack={onBack} />
    </div>
  );
}
